/* global chiliController, growlMessages */
/*
 * Copyright 2026 Collaterate. All rights reserved.
 *
 * data-profiles-selector.js
 *
 * Storefront controller for the themeable data-profiles widget rendered by
 * data_profiles.jsp.  Reads window.dataProfilesConfig set by the JSP and
 * provides:
 *
 *   mode === 'select'  (personalize page)
 *     - Populates #dataProfileSelector with the user's profiles.
 *     - On change (or "Use Profile" click) fetches field values and pushes
 *       them into the Chili editor via chiliController.applyFieldValues().
 *     - Defers until the Chili document is loaded when the editor isn't
 *       ready yet (consistent with personalizeController.js loadUserData).
 *
 *   mode === 'manage'  (account page)
 *     - Populates #dataProfileSelector read-only.
 *     // TODO (deferred): storefront add/edit/save UI for manage mode.
 *
 * REST calls use session-cookie auth (same-origin fetch, credentials: 'same-origin').
 * No Authorization header is required; the JSESSIONID cookie is sent automatically.
 */
(function () {
    'use strict';

    var cfg = window.dataProfilesConfig;
    if (!cfg || !cfg.userId) {
        // Not logged in or config missing — silently no-op.
        return;
    }

    var restBase = cfg.restBase || '/rest/storefront';
    var userId = cfg.userId;
    var siteId = cfg.siteId;
    var templateId = cfg.templateId;
    var mode = cfg.mode || 'select';

    // -------------------------------------------------------------------------
    // Helper: plain fetch wrapper with session-cookie auth (same-origin)
    // -------------------------------------------------------------------------
    function storefrontFetch(url) {
        return fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json' }
        }).then(function (response) {
            if (!response.ok) {
                throw new Error('Request failed: ' + response.status + ' ' + url);
            }
            return response.json();
        });
    }

    function storefrontPost(url, body) {
        return fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        }).then(function (response) {
            if (!response.ok) {
                return response.json().catch(function () {
                    throw new Error('Request failed: ' + response.status + ' ' + url);
                }).then(function (errBody) {
                    throw new Error((errBody && errBody.message) ? errBody.message : ('Request failed: ' + response.status));
                });
            }
            return response.json();
        });
    }

    // -------------------------------------------------------------------------
    // Helper: does the select already contain an option with this value?
    // -------------------------------------------------------------------------
    function optionExists(select, value) {
        if (!value) {
            return false;
        }
        for (var i = 0; i < select.options.length; i++) {
            if (select.options[i].value === value) {
                return true;
            }
        }
        return false;
    }

    // -------------------------------------------------------------------------
    // Populate the select element with profile options.
    //
    // Selection precedence (returns the resulting selected profile id, or null):
    //   1. opts.selectProfileId  — an explicit profile to select (e.g. one just
    //      saved via Update / Save-as-New).
    //   2. the selection that was active before this refresh — so refreshing the
    //      list after a capture does not clobber what the shopper had chosen.
    //   3. opts.autoSelectDefault — on the initial personalize-page load, select
    //      the user's default profile so it can be auto-applied to the template.
    // -------------------------------------------------------------------------
    function populateSelector(profiles, opts) {
        opts = opts || {};
        var select = document.getElementById('dataProfileSelector');
        if (!select) {
            return null;
        }

        // Remember the prior selection so a post-capture refresh can preserve it.
        var previousValue = select.value;
        select.innerHTML = '';

        if (!profiles || profiles.length === 0) {
            var empty = document.createElement('option');
            empty.value = '';
            empty.textContent = '-- No profiles available --';
            select.appendChild(empty);
            select.disabled = true;
            return null;
        }

        var placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.textContent = '-- Select a profile --';
        select.appendChild(placeholder);

        var defaultProfileId = null;
        for (var i = 0; i < profiles.length; i++) {
            var p = profiles[i];
            var opt = document.createElement('option');
            opt.value = p.id;
            opt.textContent = p.name || ('Profile ' + p.id);
            if (p.defaultProfile) {
                defaultProfileId = String(p.id);
                opt.setAttribute('data-default', 'true');
                opt.textContent += ' (default)';
            }
            select.appendChild(opt);
        }

        select.disabled = false;

        // Resolve which option should be selected (see precedence in the doc above).
        var explicitId = (opts.selectProfileId != null) ? String(opts.selectProfileId) : null;
        if (explicitId && optionExists(select, explicitId)) {
            select.value = explicitId;
        } else {
            if (explicitId) {
                console.warn('data-profiles-selector: requested selectProfileId ' + explicitId + ' not found in refreshed list');
            }
            if (previousValue && optionExists(select, previousValue)) {
                select.value = previousValue;
            } else if (opts.autoSelectDefault && defaultProfileId) {
                select.value = defaultProfileId;
            }
        }

        return select.value || null;
    }

    // -------------------------------------------------------------------------
    // Fetch profiles list: GET /rest/storefront/users/v2/{userId}/dataProfiles
    //
    // opts is forwarded to populateSelector to control selection, and (in select
    // mode) drives whether the resolved selection is auto-applied to the editor:
    //   opts.autoSelectDefault  — initial load: select + apply the default profile
    //   opts.selectProfileId    — select a specific profile (e.g. just saved)
    // -------------------------------------------------------------------------
    function fetchProfiles(opts) {
        opts = opts || {};
        var url = restBase + '/users/v2/' + userId + '/dataProfiles';
        fetchAllProfilePages(url)
            .then(function (profiles) {
                var selectedId = populateSelector(profiles, opts);
                // In select mode, push the resolved selection into the editor when
                // asked to. On first load this auto-fills the template from the
                // user's default profile (matching the backend resolveForDefault).
                if (mode === 'select' && selectedId && opts.autoSelectDefault) {
                    applyProfile(selectedId);
                }
            })
            .catch(function (err) {
                console.error('data-profiles-selector: failed to load profiles', err);
                var select = document.getElementById('dataProfileSelector');
                if (select) {
                    select.innerHTML = '<option value="">-- Error loading profiles --</option>';
                    select.disabled = true;
                }
                // Surface to the shopper so a load failure isn't just an unexplained
                // empty/disabled dropdown; degrade to console-only when growl isn't
                // loaded on this page.
                if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                    growlMessages.showErrorMessage('Could not load your saved data profiles. Please try again.');
                }
            });
    }

    function fetchAllProfilePages(baseUrl) {
        var allItems = [];
        function fetchPage(pageNumber) {
            var separator = baseUrl.indexOf('?') >= 0 ? '&' : '?';
            // Collaterate paged endpoints bind to PageRequestDTO, whose nested "page"
            // bean requires dot notation (page.number / page.size). Bracket notation
            // (page[number]) makes Spring treat "page" as indexed and 400s.
            var url = baseUrl + separator + 'page.number=' + pageNumber + '&page.size=50';
            return storefrontFetch(url).then(function (data) {
                var items = (data && data.content) ? data.content : (Array.isArray(data) ? data : []);
                allItems = allItems.concat(items);
                if (data && data.page && pageNumber < data.page.totalPages) {
                    return fetchPage(pageNumber + 1);
                }
                return allItems;
            });
        }
        return fetchPage(1);
    }

    // -------------------------------------------------------------------------
    // mode === 'select': fetch field values and push into Chili editor
    // -------------------------------------------------------------------------
    function applyProfile(profileId) {
        // No profile selected (e.g. the placeholder) is a normal no-op.
        if (!profileId) {
            return;
        }
        // A missing templateId means the page could not resolve the Chili document
        // (offering is not a Chili template share). Surface it rather than silently
        // doing nothing, so a broken personalize page is diagnosable.
        if (!templateId) {
            console.error('data-profiles-selector: cannot apply profile — templateId is not set on this page');
            if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                growlMessages.showErrorMessage('Could not apply the data profile: this product template is unavailable.');
            }
            return;
        }
        var url = restBase + '/users/v2/' + userId + '/personalize/templates/' + encodeURIComponent(templateId) + '/fieldValues?profileId=' + profileId;
        storefrontFetch(url)
            .then(function (data) {
                var content = data && data.content;
                // Prefer the typed `variables` list (each carries dataType so the
                // editor can apply text vs list vs image correctly); fall back to
                // the legacy variableValues map for older responses.
                var variables      = content && content.variables;
                var variableValues = content && content.variableValues;
                var payload = (variables && variables.length) ? variables : variableValues;
                if (!payload) {
                    return;
                }
                // Push values into the Chili editor.
                // chiliController.applyFieldValues handles deferred execution
                // when the editor document is not yet fully loaded.
                if (typeof chiliController !== 'undefined' && typeof chiliController.applyFieldValues === 'function') {
                    chiliController.applyFieldValues(payload);
                } else {
                    console.warn('data-profiles-selector: chiliController.applyFieldValues not available');
                }
            })
            .catch(function (err) {
                console.error('data-profiles-selector: failed to load field values for profile ' + profileId, err);
                // Surface to the shopper when the storefront growl utility is present;
                // degrade silently (console only) when it is not loaded on this page.
                if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                    growlMessages.showErrorMessage('Could not apply the selected data profile. Please try again.');
                }
            });
    }

    // -------------------------------------------------------------------------
    // mode === 'select': capture the editor's current values into a profile
    // (update the selected profile, or create a new one). The server maps each
    // variable to its bound enabled field and converts list ids -> display.
    // -------------------------------------------------------------------------
    function captureToProfile(profileId, name) {
        // A missing templateId means the page could not resolve the Chili document,
        // so there is nothing to capture against. Tell the shopper instead of having
        // the Update / Save-as-New buttons appear to do nothing.
        if (!templateId) {
            console.error('data-profiles-selector: cannot capture — templateId is not set on this page');
            if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                growlMessages.showErrorMessage('Could not save the data profile: this product template is unavailable.');
            }
            return Promise.resolve(false);
        }
        if (typeof chiliController === 'undefined' || typeof chiliController.readVariableValues !== 'function') {
            console.error('data-profiles-selector: chiliController.readVariableValues not available — editor not ready');
            if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                growlMessages.showErrorMessage('Could not save the data profile: the editor is not ready yet. Please wait for the template to finish loading and try again.');
            }
            return Promise.resolve(false);
        }
        return chiliController.readVariableValues()
            .then(function (vars) {
                var variables = (vars || []).map(function (v) {
                    return { variableName: v.name, value: v.value };
                });
                // Reading zero variables means the editor document is not loaded yet
                // (or has no variables); saving would wipe the profile, so abort and
                // tell the shopper rather than silently capturing nothing.
                if (!variables.length) {
                    console.error('data-profiles-selector: editor returned no variables to capture');
                    if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                        growlMessages.showErrorMessage('Could not read the template fields. Please wait for the template to finish loading and try again.');
                    }
                    return false;
                }
                var body = { profileId: profileId || null, name: name || null, variables: variables };
                var url = restBase + '/users/v2/' + userId + '/personalize/templates/'
                    + encodeURIComponent(templateId) + '/captureToProfile';
                return storefrontPost(url, body).then(function (data) {
                    var saved = data && data.content;
                    // Refresh the dropdown and keep the saved/created profile selected
                    // so the shopper can see (and keep working with) what they just saved.
                    fetchProfiles({ selectProfileId: saved ? saved.id : profileId });
                    if (typeof growlMessages !== 'undefined' && growlMessages.showInfoMessage) {
                        growlMessages.showInfoMessage('Saved data profile'
                            + (saved && saved.name ? ': ' + saved.name : '') + '.');
                    }
                    return true;
                });
            })
            .catch(function (err) {
                console.error('data-profiles-selector: capture to profile failed', err);
                if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                    growlMessages.showErrorMessage('Could not save the data profile. '
                        + (err && err.message ? err.message : ''));
                }
                return false;
            });
    }

    // -------------------------------------------------------------------------
    // Bind events (select mode only)
    // -------------------------------------------------------------------------
    function bindSelectModeEvents() {
        var select = document.getElementById('dataProfileSelector');
        var applyBtn = document.getElementById('dataProfileApplyBtn');
        var updateBtn = document.getElementById('dataProfileUpdateBtn');
        var saveAsNewBtn = document.getElementById('dataProfileSaveAsNewBtn');

        if (select) {
            select.addEventListener('change', function () {
                var profileId = select.value;
                if (profileId) {
                    applyProfile(profileId);
                }
            });
        }

        if (applyBtn) {
            applyBtn.addEventListener('click', function () {
                var profileId = select ? select.value : '';
                if (profileId) {
                    applyProfile(profileId);
                }
            });
        }

        if (updateBtn) {
            updateBtn.addEventListener('click', function () {
                var profileId = select ? select.value : '';
                if (!profileId) {
                    if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                        growlMessages.showErrorMessage('Select a profile to update.');
                    }
                    return;
                }
                captureToProfile(profileId, null);
            });
        }

        var newNameForm = document.getElementById('dataProfileNewNameForm');
        var newNameInput = document.getElementById('dataProfileNewName');
        var newNameSaveBtn = document.getElementById('dataProfileNewNameSaveBtn');
        var newNameCancelBtn = document.getElementById('dataProfileNewNameCancelBtn');

        function openNewNameForm() {
            if (!newNameForm) { return; }
            if (newNameInput) { newNameInput.value = ''; }
            newNameForm.style.display = 'inline-block';
            if (newNameInput) { newNameInput.focus(); }
        }

        function closeNewNameForm() {
            if (!newNameForm) { return; }
            newNameForm.style.display = 'none';
            if (newNameInput) { newNameInput.value = ''; }
        }

        function submitNewName() {
            var name = (newNameInput ? newNameInput.value : '').trim();
            if (!name) {
                if (typeof growlMessages !== 'undefined' && growlMessages.showErrorMessage) {
                    growlMessages.showErrorMessage('A profile name is required.');
                }
                if (newNameInput) { newNameInput.focus(); }
                return;
            }
            // Close only after a successful save, so a failed save keeps the form open
            // with the typed name rather than forcing the user to re-enter it.
            captureToProfile(null, name).then(function (ok) {
                if (ok) { closeNewNameForm(); }
            });
        }

        // "Save as New" opens the inline name entry; the explicit Save/Cancel buttons
        // (and Enter/Escape) confirm or abandon it, so the action is always reversible.
        if (saveAsNewBtn) {
            saveAsNewBtn.addEventListener('click', openNewNameForm);
        }
        if (newNameSaveBtn) {
            newNameSaveBtn.addEventListener('click', submitNewName);
        }
        if (newNameCancelBtn) {
            newNameCancelBtn.addEventListener('click', closeNewNameForm);
        }
        if (newNameInput) {
            newNameInput.addEventListener('keydown', function (e) {
                if (e.key === 'Enter') {
                    e.preventDefault();
                    submitNewName();
                } else if (e.key === 'Escape') {
                    e.preventDefault();
                    closeNewNameForm();
                }
            });
        }
    }

    // -------------------------------------------------------------------------
    // Initialise
    // -------------------------------------------------------------------------
    function init() {
        if (mode === 'select') {
            // Bind events before the first fetch so the auto-applied default and any
            // user interaction are both handled.
            bindSelectModeEvents();
            // Initial load: select the user's default profile and apply it to the
            // template so the editor is pre-filled without requiring a manual pick.
            fetchProfiles({ autoSelectDefault: true });
        } else {
            fetchProfiles();
        }

        // TODO (deferred): storefront add/edit/save UI for manage mode.
        // When mode === 'manage', this will expose add/edit/delete controls
        // for user data profiles.  The REST endpoints (POST/PUT/DELETE on
        // /rest/storefront/users/v2/{userId}/dataProfiles) are already
        // available; only the front-end workflow is deferred.
    }

    // Run after DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

}());
